import React, { useEffect, useState } from "react";
import { AvatarChip } from "./AvatarChip";
import { fetchFriends, removeFriend, blockUser, unblockUser, fetchBlocks } from "../lib/api";
import { VerifiedCheck } from "./VerifiedCheck";

/**
 * FriendsModal — manage friends + block list in one place.
 *
 * Two tabs:
 *  - FRIENDS: list of accepted friendships with REMOVE / BLOCK / DM actions.
 *  - BLOCKED: list of users I've blocked with UNBLOCK action.
 *
 * Opened from the dashboard FRIENDS tile.
 */
export function FriendsModal({ open, onClose, onToast, onOpenDm }) {
  const [tab, setTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [f, b] = await Promise.all([fetchFriends(), fetchBlocks()]);
      setFriends(f || []);
      setBlocks(b || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load friends.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { reload(); } /* eslint-disable-next-line */ }, [open]);

  if (!open) return null;

  const onRemove = async (u) => {
    if (!window.confirm(`Remove ${u.name} from friends?`)) return;
    setBusyId(u.user_id);
    try {
      await removeFriend(u.user_id);
      onToast?.(`Removed ${u.name}.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't remove.");
    } finally { setBusyId(""); }
  };

  const onBlock = async (u) => {
    if (!window.confirm(
      `Block ${u.name}? This removes them from your friends and prevents DMs.`
    )) return;
    setBusyId(u.user_id);
    try {
      await blockUser(u.user_id);
      onToast?.(`🚫 ${u.name} blocked.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't block.");
    } finally { setBusyId(""); }
  };

  const onUnblock = async (b) => {
    setBusyId(b.blocked_user_id);
    try {
      await unblockUser(b.blocked_user_id);
      onToast?.(`✅ Unblocked ${b.blocked_name || "player"}.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't unblock.");
    } finally { setBusyId(""); }
  };

  return (
    <div
      data-testid="friends-modal"
      className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-strong neon-border-[#14b8a6] rounded-3xl w-full max-w-lg p-5 flex flex-col"
        style={{ maxHeight: "85vh" }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-user-group mr-1" />MY NETWORK
            </p>
            <p className="font-display text-2xl mt-0.5">FRIENDS &amp; BLOCKS</p>
          </div>
          <button
            onClick={onClose}
            data-testid="friends-modal-close"
            className="w-9 h-9 rounded-xl btn-ghost"
          >
            <i className="fas fa-xmark" />
          </button>
        </div>

        <div className="flex gap-1 mb-3" data-testid="friends-modal-tabs">
          <button
            type="button"
            onClick={() => setTab("friends")}
            data-testid="friends-tab-friends"
            className={`flex-1 text-[11px] tracking-widest px-3 py-2 rounded-xl ${
              tab === "friends" ? "btn-neon" : "btn-ghost"
            }`}
          >
            FRIENDS · {friends.length}
          </button>
          <button
            type="button"
            onClick={() => setTab("blocked")}
            data-testid="friends-tab-blocked"
            className={`flex-1 text-[11px] tracking-widest px-3 py-2 rounded-xl ${
              tab === "blocked" ? "btn-neon" : "btn-ghost"
            }`}
          >
            BLOCKED · {blocks.length}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="friends-modal-list">
          {loading ? (
            <p className="text-[12px] text-white/50 text-center mt-6">Loading…</p>
          ) : tab === "friends" ? (
            friends.length === 0 ? (
              <p className="text-[12px] text-white/50 text-center mt-6" data-testid="friends-empty">
                No friends yet — explore the PLAYERS directory and send a friend request.
              </p>
            ) : friends.map((u) => (
              <div
                key={u.user_id}
                data-testid={`friend-row-${u.user_id}`}
                className="flex items-center gap-2 p-2 rounded-xl bg-black/30 border border-white/10"
              >
                <AvatarChip avatarId={u.avatar_id} pfpUrl={u.pfp_url} size={36} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate flex items-center gap-1.5">
                    <span className="truncate">{u.name || "Operator"}</span>
                    <VerifiedCheck user={u} size={13} />
                    {(u.user_type === "pro" || u.plan === "elite") && (
                      <span className="text-[9px] tracking-widest bg-[#14b8a6]/20 text-[#14b8a6] px-1.5 py-0.5 rounded-full border border-[#14b8a6]/40">
                        PRO
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-[#a3a3ac]">
                    {u.game || "Multi-game"} · MMR {u.elo || "—"}
                  </p>
                </div>
                {onOpenDm && (u.messaging_policy !== "off" && u.messaging_enabled !== false) && (
                  <button
                    type="button"
                    onClick={() => { onOpenDm?.(u); onClose?.(); }}
                    data-testid={`friend-dm-${u.user_id}`}
                    title="Send DM"
                    className="w-8 h-8 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 flex items-center justify-center"
                  >
                    <i className="fas fa-envelope text-[11px]" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onRemove(u)}
                  disabled={busyId === u.user_id}
                  data-testid={`friend-remove-${u.user_id}`}
                  title="Remove friend"
                  className="w-8 h-8 rounded-lg bg-black/40 text-white/70 border border-white/15 hover:border-white/40 disabled:opacity-50 flex items-center justify-center"
                >
                  <i className="fas fa-user-minus text-[11px]" />
                </button>
                <button
                  type="button"
                  onClick={() => onBlock(u)}
                  disabled={busyId === u.user_id}
                  data-testid={`friend-block-${u.user_id}`}
                  title="Block user"
                  className="w-8 h-8 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50 flex items-center justify-center"
                >
                  <i className="fas fa-ban text-[11px]" />
                </button>
              </div>
            ))
          ) : (
            blocks.length === 0 ? (
              <p className="text-[12px] text-white/50 text-center mt-6" data-testid="blocked-empty">
                You haven't blocked anyone.
              </p>
            ) : blocks.map((b) => (
              <div
                key={b.blocked_user_id}
                data-testid={`block-row-${b.blocked_user_id}`}
                className="flex items-center gap-2 p-2 rounded-xl bg-black/30 border border-[#14b8a6]/20"
              >
                <div className="w-9 h-9 rounded-full bg-[#14b8a6]/15 border border-[#14b8a6]/40 flex items-center justify-center">
                  <i className="fas fa-ban text-[#14b8a6]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{b.blocked_name || b.blocked_user_id}</p>
                  <p className="text-[10px] text-white/45">
                    Blocked {b.created_at ? new Date(b.created_at).toLocaleDateString() : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onUnblock(b)}
                  disabled={busyId === b.blocked_user_id}
                  data-testid={`block-unblock-${b.blocked_user_id}`}
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50"
                >
                  UNBLOCK
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default FriendsModal;
